import { useTranslation } from 'react-i18next';
import type { AuditCategory, AuditLogEntry } from '@/api/audit/auditApi.types';
import { Badge } from '@/components/ui/badge';
import { Icon, type IconName } from '@/components/common/Icon';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import {
  describeActivityAction,
  describeActivityActor,
  describeActivityDevice,
  describeActivitySubjects,
} from './activityDescription';

const CATEGORY_ICON: Record<AuditCategory, IconName> = {
  access: 'Eye',
  change: 'Edit',
  authentication: 'LogIn',
};

interface ActivityEntryRowProps {
  entry: AuditLogEntry;
  showWorkspace: boolean;
  compact?: boolean;
  showClient?: boolean;
}

export function ActivityEntryRow({
  entry,
  showWorkspace,
  compact = false,
  showClient = true,
}: ActivityEntryRowProps) {
  const { t } = useTranslation('activity');
  const subjects = describeActivitySubjects(entry, t, showClient);
  const deviceLabel = describeActivityDevice(entry, t);
  const workspaceLabel = entry.tenantName === null ? t('workspace.platform') : entry.tenantName;

  return (
    <li className={compact === true ? 'py-3' : 'px-4 sm:px-5 py-4'}>
      <div className="flex items-start gap-3">
        <span
          className="w-8 h-8 rounded-lg bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0"
          title={t('category.' + entry.category)}
          aria-label={t('category.' + entry.category)}
        >
          <Icon name={CATEGORY_ICON[entry.category]} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-x-3 gap-y-1 flex-wrap">
            <p className="text-[13px] text-zooindigo min-w-0">
              <span className="font-semibold">{describeActivityActor(entry, t)}</span>
              <span className="text-zoomuted"> · </span>
              <span>{describeActivityAction(entry, t)}</span>
            </p>
            <time className="text-[11px] text-zoomuted shrink-0" dateTime={entry.createdAt}>
              {formatIsoDateTimeToTimestamp(entry.createdAt)}
            </time>
          </div>
          {subjects.length === 0 ? null : (
            <p className="text-[12px] text-zooindigo mt-0.5 break-words">{subjects.join(' · ')}</p>
          )}
          <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-[11px] text-zoomuted mt-1">
            {showWorkspace === true ? (
              <span className="inline-flex items-center gap-1 min-w-0">
                <Icon name="Building" />
                <span className="truncate">{workspaceLabel}</span>
              </span>
            ) : null}
            {deviceLabel === null ? null : (
              <span className="inline-flex items-center gap-1">
                <Icon name="Monitor" />
                {deviceLabel}
              </span>
            )}
            {entry.ipAddress === null ? null : (
              <span>{t('networkAddress', { address: entry.ipAddress })}</span>
            )}
            {entry.isElevatedAccess === true ? (
              <Badge tone="warning" shape="tag" size="sm" className="text-[11px]">
                {t('platformSupport')}
              </Badge>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
}
